/**
 * Capa de Persistencia Local (IndexedDB) y Sincronización Remota (MySQL - XAMPP)
 * Administra las tablas 'scans' y 'blacklist' y redirige a la API MySQL si está activado.
 * Cumple con .idx-rules.md: modular y desacoplado, funciones < 40 líneas.
 */

import { getConfig } from '../services/config.js';

const DB_NAME = 'DNILectorDB';
const DB_VERSION = 5;

/**
 * Helper para realizar peticiones fetch a la API de PHP MySQL.
 */
async function mysqlFetch(action, method = 'GET', body = null) {
  const config = getConfig();
  const url = `${config.apiUrl}?action=${action}`;
  const options = {
    method,
    headers: {
      'Content-Type': 'application/json'
    }
  };
  if (body) {
    options.body = JSON.stringify(body);
  }
  const response = await fetch(url, options);
  if (!response.ok) {
    throw new Error(`Error en API MySQL: ${response.statusText}`);
  }
  const data = await response.json();
  if (data && data.error) {
    throw new Error(data.error);
  }
  return data;
}

/**
 * Abre la conexión local a IndexedDB.
 */
export function openDB() {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = event.target.result;
      
      // Crear scans si no existe
      if (!db.objectStoreNames.contains('scans')) {
        const scanStore = db.createObjectStore('scans', { keyPath: 'id', autoIncrement: true });
        scanStore.createIndex('hashedDni', 'hashedDni', { unique: false });
        scanStore.createIndex('timestamp', 'timestamp', { unique: false });
      }
      
      // Recrear blacklist para soportar clave primaria compuesta (hashedDni, company_id)
      if (db.objectStoreNames.contains('blacklist')) {
        db.deleteObjectStore('blacklist');
      }
      db.createObjectStore('blacklist', { keyPath: ['hashedDni', 'company_id'] });

      // Recrear users para sembrar usuarios SaaS
      if (db.objectStoreNames.contains('users')) {
        db.deleteObjectStore('users');
      }
      const userStore = db.createObjectStore('users', { keyPath: 'username' });
      userStore.add({ username: 'superadmin', fullName: 'Super Administrador Global', role: 'superadmin', password: 'superadmin', status: 'ACTIVE', company_id: null, createdAt: new Date().toISOString() });
      userStore.add({ username: 'superadmin1', fullName: 'SuperAdmin Empresa A', role: 'superadmin', password: 'superadmin', status: 'ACTIVE', company_id: 1, createdAt: new Date().toISOString() });
      userStore.add({ username: 'superadmin2', fullName: 'SuperAdmin Empresa B', role: 'superadmin', password: 'superadmin', status: 'ACTIVE', company_id: 2, createdAt: new Date().toISOString() });
      userStore.add({ username: 'admin', fullName: 'Administrador Puerta A', role: 'admin', password: 'admin', status: 'ACTIVE', company_id: 1, createdAt: new Date().toISOString() });
      userStore.add({ username: 'adminb', fullName: 'Administrador Puerta B', role: 'admin', password: 'admin', status: 'ACTIVE', company_id: 2, createdAt: new Date().toISOString() });
      userStore.add({ username: 'portero', fullName: 'Portero Acceso A', role: 'portero', password: 'portero', status: 'ACTIVE', company_id: 1, createdAt: new Date().toISOString() });

      // Crear store de companies
      if (db.objectStoreNames.contains('companies')) {
        db.deleteObjectStore('companies');
      }
      const companyStore = db.createObjectStore('companies', { keyPath: 'id', autoIncrement: true });
      companyStore.add({ id: 1, name: 'Empresa Demo A', status: 'ACTIVE', createdAt: new Date().toISOString() });
      companyStore.add({ id: 2, name: 'Empresa Demo B', status: 'ACTIVE', createdAt: new Date().toISOString() });
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Agrega un nuevo registro de escaneo.
 */
export async function saveScanLog(scanData) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    const record = {
      ...scanData,
      timestamp: new Date().toISOString().slice(0, 19).replace('T', ' ')
    };
    return mysqlFetch('save_scan', 'POST', record);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('scans', 'readwrite');
    const store = tx.objectStore('scans');
    const request = store.add({
      ...scanData,
      timestamp: new Date().toISOString()
    });
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Recupera todos los registros de escaneo ordenados, filtrados por empresa.
 */
export async function getAllScans(companyId) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`get_scans&company_id=${companyId}`);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('scans', 'readonly');
    const store = tx.objectStore('scans');
    const request = store.getAll();
    request.onsuccess = () => {
      const scans = request.result || [];
      if (companyId !== undefined && companyId !== null && companyId !== '') {
        const compIdInt = parseInt(companyId, 10);
        resolve(scans.filter(s => parseInt(s.company_id, 10) === compIdInt));
      } else {
        resolve(scans);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Cuenta cuántas veces ha ingresado un DNI en los últimos 30 días para una empresa específica.
 */
export async function countVisitsLastMonth(hashedDni, companyId) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    const scans = await getAllScans(companyId);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    const dateString = thirtyDaysAgo.toISOString().slice(0, 19).replace('T', ' ');
    
    const recent = scans.filter(s => s.hashedDni === hashedDni && s.timestamp >= dateString && s.status === 'ALLOWED');
    return recent.length;
  }

  const db = await openDB();
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
  const dateString = thirtyDaysAgo.toISOString();

  return new Promise((resolve, reject) => {
    const tx = db.transaction('scans', 'readonly');
    const store = tx.objectStore('scans');
    const index = store.index('hashedDni');
    const request = index.getAll(IDBKeyRange.only(hashedDni));
    request.onsuccess = () => {
      const scans = request.result || [];
      const compIdInt = companyId ? parseInt(companyId, 10) : null;
      const recentScans = scans.filter(s => 
        s.timestamp >= dateString && 
        s.status === 'ALLOWED' && 
        (compIdInt === null || parseInt(s.company_id, 10) === compIdInt)
      );
      resolve(recentScans.length);
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Agrega un DNI a la lista negra de una empresa específica.
 */
export async function addToBlacklist(hashedDni, fullName, reason, companyId) {
  const config = getConfig();
  const compIdInt = companyId ? parseInt(companyId, 10) : null;
  if (config.dbMode === 'mysql') {
    return mysqlFetch('add_blacklist', 'POST', { hashedDni, fullName, reason, company_id: compIdInt });
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('blacklist', 'readwrite');
    const store = tx.objectStore('blacklist');
    const request = store.put({
      hashedDni,
      company_id: compIdInt,
      fullName,
      reason,
      createdAt: new Date().toISOString()
    });
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Remueve un DNI de la lista negra de una empresa.
 */
export async function removeFromBlacklist(hashedDni, companyId) {
  const config = getConfig();
  const compIdInt = companyId ? parseInt(companyId, 10) : null;
  if (config.dbMode === 'mysql') {
    return mysqlFetch('remove_blacklist', 'POST', { hash: hashedDni, company_id: compIdInt });
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('blacklist', 'readwrite');
    const store = tx.objectStore('blacklist');
    const request = store.delete([hashedDni, compIdInt]);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Comprueba si un hash de DNI se encuentra en la lista negra de una empresa.
 */
export async function checkBlacklist(hashedDni, companyId) {
  const config = getConfig();
  const compIdInt = companyId ? parseInt(companyId, 10) : null;
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`check_blacklist&hash=${encodeURIComponent(hashedDni)}&company_id=${compIdInt}`);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('blacklist', 'readonly');
    const store = tx.objectStore('blacklist');
    const request = store.get([hashedDni, compIdInt]);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Recupera todos los elementos de la lista negra.
 */
export async function getBlacklist(companyId) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`get_blacklist&company_id=${companyId}`);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('blacklist', 'readonly');
    const store = tx.objectStore('blacklist');
    const request = store.getAll();
    request.onsuccess = () => {
      const items = request.result || [];
      if (companyId !== undefined && companyId !== null && companyId !== '') {
        const compIdInt = parseInt(companyId, 10);
        resolve(items.filter(x => parseInt(x.company_id, 10) === compIdInt));
      } else {
        resolve(items);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Genera 50 registros de prueba distribuidos en el tiempo para una empresa.
 */
export async function generateMockScans(companyId) {
  const config = getConfig();
  const compIdInt = companyId ? parseInt(companyId, 10) : null;
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`generate_mock_scans&company_id=${compIdInt}`, 'POST');
  }

  const db = await openDB();
  const tx = db.transaction('scans', 'readwrite');
  const store = tx.objectStore('scans');
  const bouncers = ['admin', 'portero', 'Juan', 'Carlos'];
  const genders = ['M', 'F'];
  const statusOptions = ['ALLOWED', 'ALLOWED', 'ALLOWED', 'REJECTED'];
  const rejectionReasons = ['UNDERAGE', 'BLACKLIST', 'CAPACITY_FULL'];

  const now = new Date();
  for (let i = 0; i < 50; i++) {
    const hoursOffset = Math.floor(Math.random() * 12);
    const minutesOffset = Math.floor(Math.random() * 60);
    const timestamp = new Date(now.getTime() - (hoursOffset * 3600 * 1000) - (minutesOffset * 60 * 1000));

    const status = statusOptions[Math.floor(Math.random() * statusOptions.length)];
    const gender = genders[Math.floor(Math.random() * genders.length)];
    const age = Math.floor(Math.random() * 28) + 18;
    const birthYear = now.getFullYear() - age;
    
    const record = {
      hashedDni: 'mockhash' + Math.floor(Math.random() * 10),
      gender,
      birthDate: `${birthYear}-05-15`,
      age,
      status,
      portero: bouncers[Math.floor(Math.random() * bouncers.length)],
      scannerType: 'SIM_PDF417',
      company_id: compIdInt,
      timestamp: timestamp.toISOString()
    };

    if (status === 'REJECTED') {
      record.rejectionReason = rejectionReasons[Math.floor(Math.random() * rejectionReasons.length)];
      if (record.rejectionReason === 'UNDERAGE') {
        record.age = Math.floor(Math.random() * 5) + 13;
        record.birthDate = `${now.getFullYear() - record.age}-05-15`;
      }
    }
    store.add(record);
  }

  return new Promise((resolve, reject) => {
    tx.oncomplete = () => resolve(true);
    tx.onerror = () => reject(tx.error);
  });
}

/**
 * Limpia los registros de escaneo de una empresa específica.
 */
export async function clearAllScans(companyId) {
  const config = getConfig();
  const compIdInt = companyId ? parseInt(companyId, 10) : null;
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`clear_all_scans&company_id=${compIdInt}`, 'POST');
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(['scans'], 'readwrite');
    const store = tx.objectStore('scans');
    
    if (compIdInt !== null) {
      // Borrado selectivo mediante cursor
      const request = store.openCursor();
      request.onsuccess = (event) => {
        const cursor = event.target.result;
        if (cursor) {
          if (parseInt(cursor.value.company_id, 10) === compIdInt) {
            cursor.delete();
          }
          cursor.continue();
        } else {
          resolve(true);
        }
      };
      request.onerror = () => reject(request.error);
    } else {
      // Borrar todos
      const request = store.clear();
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    }
  });
}

/**
 * Obtiene todos los usuarios registrados.
 */
export async function getAllUsers(companyId) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`get_users&company_id=${companyId}`);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('users', 'readonly');
    const store = tx.objectStore('users');
    const request = store.getAll();
    request.onsuccess = () => {
      const users = request.result || [];
      if (companyId !== undefined && companyId !== null && companyId !== '') {
        const compIdInt = parseInt(companyId, 10);
        resolve(users.filter(u => parseInt(u.company_id, 10) === compIdInt));
      } else {
        resolve(users);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Guarda o actualiza un usuario.
 */
export async function saveUser(userData) {
  const config = getConfig();
  const record = {
    ...userData,
    company_id: userData.company_id ? parseInt(userData.company_id, 10) : null,
    createdAt: userData.createdAt || new Date().toISOString()
  };
  if (config.dbMode === 'mysql') {
    return mysqlFetch('save_user', 'POST', record);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('users', 'readwrite');
    const store = tx.objectStore('users');
    
    if (!userData.isEdit) {
      const checkReq = store.get(userData.username);
      checkReq.onsuccess = () => {
        if (checkReq.result) {
          reject(new Error('El nombre de usuario ya está registrado.'));
        } else {
          const request = store.add(record);
          request.onsuccess = () => resolve(true);
          request.onerror = () => reject(request.error);
        }
      };
      checkReq.onerror = () => reject(checkReq.error);
    } else {
      const request = store.put(record);
      request.onsuccess = () => resolve(true);
      request.onerror = () => reject(request.error);
    }
  });
}

/**
 * Elimina un usuario por su username.
 */
export async function deleteUser(username) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch('delete_user', 'POST', { username });
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('users', 'readwrite');
    const store = tx.objectStore('users');
    const request = store.delete(username);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Verifica las credenciales de inicio de sesión de un usuario.
 */
export async function verifyUserCredentials(username, password) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`verify_user&username=${encodeURIComponent(username)}&password=${encodeURIComponent(password)}`);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('users', 'readonly');
    const store = tx.objectStore('users');
    const request = store.get(username);
    request.onsuccess = () => {
      const user = request.result;
      if (user && user.password === password && user.status === 'ACTIVE') {
        resolve(user);
      } else {
        resolve(null);
      }
    };
    request.onerror = () => reject(request.error);
  });
}

/**
 * Obtiene todas las empresas registradas.
 */
export async function getAllCompanies() {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch('get_companies');
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('companies', 'readonly');
    const store = tx.objectStore('companies');
    const request = store.getAll();
    request.onsuccess = () => resolve(request.result || []);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Guarda o actualiza una empresa en el sistema.
 */
export async function saveCompany(companyData) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch('save_company', 'POST', companyData);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('companies', 'readwrite');
    const store = tx.objectStore('companies');
    
    // Asignar ID autoincremental local si no se provee
    const record = {
      ...companyData,
      id: companyData.id ? parseInt(companyData.id, 10) : undefined,
      createdAt: companyData.createdAt || new Date().toISOString()
    };
    
    // Si es una inserción local y no tiene ID, lo agregamos para autoincremento
    const request = record.id ? store.put(record) : store.add(record);
    request.onsuccess = () => resolve(true);
    request.onerror = () => reject(request.error);
  });
}

/**
 * Obtiene la información actual de un usuario (para verificar si sigue activo o si cambió su rol).
 */
export async function getUserInfo(username) {
  const config = getConfig();
  if (config.dbMode === 'mysql') {
    return mysqlFetch(`get_user_info&username=${encodeURIComponent(username)}`);
  }

  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction('users', 'readonly');
    const store = tx.objectStore('users');
    const request = store.get(username);
    request.onsuccess = () => resolve(request.result || null);
    request.onerror = () => reject(request.error);
  });
}
